// KYT v2 · BEFORE A DEPOSIT — the operator's own question (5 Oct 2026: "I also need to know if it appears on sanctions
// before depositing them to my CEX"), one tap off the list (?deposit=1) instead of a panel on the default view. One row a
// desk wallet: the lane's answer word, the wallet, the strongest reason, the reader's state, every count not fully
// checked — and the rest behind Details. The words, the answers and the "not read in full" rule are Kyt.tsx's, imported.
import { Fragment } from 'react';
import type { KytIncoming, KytIncomingWallet, KytDepositAnswer } from '../../../lib/api';
import { Pill } from '../../kit';
import { DetailSheet, Pending, StateLine } from '../kit';
import { quiet, useSheetFocus } from './parts';
import { DEPOSIT_WORD, depositNote, drawnAnswer, paymentsUnread, readerTrouble, windowLabel } from '../../../screens/Kyt';

function AnswerRow({ w, answer, readFor, settled }: { w: KytIncomingWallet; answer: KytDepositAnswer; readFor: string | null; settled: boolean }) {
  const a = DEPOSIT_WORD[answer];
  const { lead, shortfalls, explain, rest } = depositNote(w, answer);
  const trouble = readerTrouble(w);
  const ofTheWindow = answer !== 'not_read' && answer !== 'not_reading';
  const period = readFor && (answer === 'no_payments' || (!settled && ofTheWindow) || (answer === 'not_reading' && shortfalls.length > 0)) ? readFor : null;
  const behind = [...trouble.flatMap((t) => t.raw), explain, rest.length > 0 ? rest.join(' · ') : null].filter((x): x is string => x !== null);
  return (
    <li className="space-y-1 border-b border-stroke-soft py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={quiet(a.tone)}>{a.word}</Pill>
        <span className="min-w-0 break-words text-body">{w.label ?? `${w.chain} wallet`}</span>
        {period && <span className="text-meta text-tg-hint">· {period}</span>}
      </div>
      {lead && <p className="text-meta">{lead}</p>}
      {trouble.map((t) => <p key={t.state} role={t.tone === 'bad' ? 'alert' : undefined} className={`text-meta ${t.tone === 'bad' ? 'text-neg' : 'text-warn'}`}>{t.state}</p>)}
      {shortfalls.length > 0 && (
        <p className="text-meta text-tg-hint">
          Not fully checked: {shortfalls.map((t, i) => <Fragment key={t}>{i > 0 && ' · '}<span className="whitespace-nowrap">{t}</span></Fragment>)}
        </p>
      )}
      {behind.length > 0 && (
        <details className="text-meta text-tg-hint">
          <summary className="tap-44 cursor-pointer">Details</summary>
          <div className="mt-1 space-y-1">{behind.map((t) => <p key={t}>{t}</p>)}</div>
        </details>
      )}
    </li>
  );
}

export function DepositSheet({ inc, fresh, words, onClose }: { inc: KytIncoming | undefined; fresh: boolean; words: string; onClose: () => void }) {
  useSheetFocus(true);
  // The answers are for the window the RESPONSE is of: while another window is read, a calm answer of the old days waits.
  const settled = !!inc && fresh;
  const readFor = inc ? windowLabel(inc) : null;
  const loud = inc ? inc.wallets.filter((w) => w.deposit.answer !== 'none') : [];
  const allRead = !!inc && !inc.wallets.some(paymentsUnread);
  return (
    <DetailSheet title="Before a deposit" onClose={onClose}>
      <p className="mb-3 text-meta text-tg-hint">Sanctions appearance, {words}. Exchanges screen with their own tools.</p>
      {!inc && <Pending inline what="Reading the payments…" />}
      {inc && !settled && <p className="text-meta text-tg-hint">Reading {words}…</p>}
      {inc && inc.wallets.length === 0 && <StateLine tone="warn">No wallet watched — nothing is read.</StateLine>}
      {/* The calm answer is said once, and only when every wallet is read in full and every answer is the calm one. */}
      {inc && settled && inc.wallets.length > 0 && loud.length === 0 && allRead && (
        <p className="mb-2 flex flex-wrap items-center gap-2"><Pill>No sanctions appearance</Pill><span className="text-meta text-tg-hint">every desk wallet</span></p>
      )}
      {inc && (
        <ul>
          {inc.wallets.map((w) => {
            const answer = drawnAnswer(w);
            if (answer === 'none' || (!settled && answer === 'no_payments')) return null;
            return <AnswerRow key={`${w.chain}:${w.address}`} w={w} answer={answer} readFor={readFor} settled={settled} />;
          })}
        </ul>
      )}
    </DetailSheet>
  );
}
